import { useState } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { CheckCircle, Clock, AlertCircle, MessageCircle, RefreshCw, Search, User } from 'lucide-react';
import { toast } from 'sonner';
import adminService from '../services/adminService';

interface Ticket {
  id: string;
  _id?: string;
  subject: string;
  category: string;
  status: 'open' | 'in-progress' | 'resolved' | 'closed';
  createdAt: Date;
  user?: { name: string; email: string };
  description?: string;
}

interface SupportTicketsProps {
  tickets: Ticket[];
  onRefresh: () => void;
  loading?: boolean;
}

const STATUS_STYLES: Record<string, string> = {
  open: 'bg-blue-50 text-blue-700 border-blue-200',
  'in-progress': 'bg-yellow-50 text-yellow-700 border-yellow-200',
  resolved: 'bg-green-50 text-green-700 border-green-200',
  closed: 'bg-gray-100 text-gray-500 border-gray-200',
};

function timeAgo(date: Date): string {
  const diff = Date.now() - new Date(date).getTime();
  if (isNaN(diff)) return '—';
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

export function SupportTickets({ tickets, onRefresh, loading }: SupportTicketsProps) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  const getTicketId = (t: Ticket) => t._id || t.id;

  const filtered = tickets.filter(t => {
    const q = search.toLowerCase();
    const matchesSearch = !q || t.subject?.toLowerCase().includes(q) || t.user?.name?.toLowerCase().includes(q) || t.user?.email?.toLowerCase().includes(q);
    const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const counts = {
    all: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    'in-progress': tickets.filter(t => t.status === 'in-progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
    closed: tickets.filter(t => t.status === 'closed').length,
  };

  const handleResolve = async (ticket: Ticket) => {
    const id = getTicketId(ticket);
    setResolvingId(id);
    try {
      await adminService.resolveTicket(id);
      toast.success('Ticket resolved');
      onRefresh();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to resolve ticket');
    } finally {
      setResolvingId(null);
    }
  };

  const toggleReply = (ticket: Ticket) => {
    const id = getTicketId(ticket);
    setReplyingTo(prev => (prev === id ? null : id));
    setReplyText('');
  };

  const handleSendReply = async (ticket: Ticket) => {
    const id = getTicketId(ticket);
    const content = replyText.trim();
    if (!content) { toast.error('Reply cannot be empty'); return; }
    setSendingReply(true);
    try {
      await adminService.replyToTicket(id, content);
      toast.success('Reply sent');
      setReplyingTo(null);
      setReplyText('');
      onRefresh();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total', value: counts.all, icon: MessageCircle, color: 'text-purple-600' },
          { label: 'Open', value: counts.open, icon: AlertCircle, color: 'text-blue-600' },
          { label: 'In Progress', value: counts['in-progress'], icon: Clock, color: 'text-yellow-600' },
          { label: 'Resolved', value: counts.resolved, icon: CheckCircle, color: 'text-green-600' },
        ].map(s => (
          <div key={s.label} className="p-4 rounded-xl border border-gray-100 bg-white">
            <p className="text-2xl font-bold text-gray-900">{s.value}</p>
            <p className="text-xs text-gray-400 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by subject or user…"
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-purple-400"
          />
        </div>
        <div className="flex items-center gap-2">
          {(['all', 'open', 'in-progress', 'resolved', 'closed'] as const).map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-xs rounded-full border transition-all capitalize ${
                statusFilter === s ? 'bg-purple-600 text-white border-purple-600' : 'border-gray-200 text-gray-500 hover:border-gray-300'
              }`}
            >
              {s === 'all' ? `All (${counts.all})` : `${s.replace('-', ' ')} (${counts[s as keyof typeof counts] ?? 0})`}
            </button>
          ))}
          <button onClick={onRefresh} disabled={loading} className="p-1.5 text-gray-400 hover:text-purple-600">
            <RefreshCw className={`size-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Ticket List */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <MessageCircle className="size-10 mx-auto mb-3 text-gray-200" />
          <p className="text-sm">{tickets.length === 0 ? 'No tickets yet' : 'No tickets match your search'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(ticket => {
            const id = getTicketId(ticket);
            const isClosed = ticket.status === 'resolved' || ticket.status === 'closed';
            const isReplying = replyingTo === id;
            return (
              <div key={id} className="rounded-xl border border-gray-100 bg-white hover:border-gray-200 transition-colors">
                <div className="flex items-center gap-4 p-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-gray-300">#{id.slice(-6).toUpperCase()}</span>
                      <Badge className={`text-xs capitalize border ${STATUS_STYLES[ticket.status]}`}>
                        {ticket.status.replace('-', ' ')}
                      </Badge>
                      {ticket.category && (
                        <span className="text-xs text-gray-400">{ticket.category}</span>
                      )}
                    </div>
                    <p className="text-sm font-medium text-gray-900 truncate">{ticket.subject}</p>
                    {ticket.user && (
                      <div className="flex items-center gap-1 mt-1">
                        <User className="size-3 text-gray-300" />
                        <span className="text-xs text-gray-400">{ticket.user.name} · {ticket.user.email}</span>
                      </div>
                    )}
                    {ticket.description && (
                      <p className="text-xs text-gray-400 mt-1 truncate">{ticket.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-xs text-gray-400">{timeAgo(ticket.createdAt)}</span>
                    {!isClosed && (
                      <Button
                        size="sm"
                        variant="outline"
                        className={`text-xs gap-1 ${isReplying ? 'border-purple-300 text-purple-700 bg-purple-50' : 'border-purple-200 text-purple-700 hover:bg-purple-50'}`}
                        onClick={() => toggleReply(ticket)}
                        disabled={sendingReply}
                      >
                        <MessageCircle className="size-3" />
                        Reply
                      </Button>
                    )}
                    {!isClosed && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs border-green-200 text-green-700 hover:bg-green-50 gap-1"
                        onClick={() => handleResolve(ticket)}
                        disabled={resolvingId === id}
                      >
                        {resolvingId === id
                          ? <div className="size-3 border border-green-600 border-t-transparent rounded-full animate-spin" />
                          : <CheckCircle className="size-3" />
                        }
                        Resolve
                      </Button>
                    )}
                  </div>
                </div>

                {isReplying && (
                  <div className="px-4 pb-4 pt-1 border-t border-gray-50">
                    <textarea
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      placeholder="Type your reply to the user…"
                      rows={3}
                      autoFocus
                      className="w-full mt-3 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-purple-400 resize-y"
                    />
                    <div className="flex items-center justify-end gap-2 mt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs"
                        onClick={() => { setReplyingTo(null); setReplyText(''); }}
                        disabled={sendingReply}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        className="text-xs bg-purple-600 hover:bg-purple-700 text-white gap-1"
                        onClick={() => handleSendReply(ticket)}
                        disabled={sendingReply || !replyText.trim()}
                      >
                        {sendingReply
                          ? <div className="size-3 border border-white border-t-transparent rounded-full animate-spin" />
                          : <MessageCircle className="size-3" />
                        }
                        Send Reply
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {filtered.length > 0 && (
        <p className="text-xs text-gray-400 text-center">
          Showing {filtered.length} of {tickets.length} tickets
        </p>
      )}
    </div>
  );
}
